import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  endVoiceSession as endVoiceSessionApi,
  mintVoiceSession,
  postVoiceTurnLatency,
  synthesizeVoiceTts,
  transcribeDictationAudio,
} from '@/api/speak2me-api';
import type { VoiceSessionMintResponse, VoiceTurnLatencyRequest } from '@vivport/backend/types';
import { detectStreamingSupport, openSttStream, type SttStreamHandle, type SttTurn } from './voice/stt-stream';
import {
  INTERRUPT_TRANSCRIPT_MIN_LENGTH,
  SPEECH_ECHO_TAIL_MS,
  capSpeakableSentences,
  matchesSpokenScript,
  normalizeSpokenWords,
  splitSpeakableSentences,
  toSpeakableText,
  type SpokenScript,
} from './voice/spoken-text';

export type VoiceSessionMode = 'off' | 'dictation' | 'conversation';
export type VoiceSessionStatus = 'idle' | 'listening' | 'speaking' | 'suspended' | 'finalizing';
export type VoiceSessionError =
  | 'permission-denied'
  | 'transcription-failed'
  | 'voice-session-failed'
  | 'tts-failed'
  | null;

export interface AudioInputDevice {
  deviceId: string;
  label: string;
}

export interface ComposerDictation {
  supported: boolean;
  active: boolean;
  /** Mic acquired and session minting, before capture is live. The button must not accept a second click. */
  starting: boolean;
  finalizing: boolean;
  analyser: AnalyserNode | null;
  selectedDeviceId: string | null;
  listDevices: () => Promise<AudioInputDevice[]>;
  selectDevice: (id: string | null) => void;
  start: () => void;
  accept: () => void;
  cancel: () => void;
}

interface UseVoiceSessionOptions {
  enabled: boolean;
  suspended: boolean;
  dictationDeviceId: string | null;
  activeNeoChatId: number | undefined;
  onNeoChatBound: (neoChatId: number) => void;
  onDictationText: (text: string, isFinal: boolean) => void;
  onUtterance: (text: string) => void;
  /** Returns true when the in-flight turn was actually aborted, false when the panel declined. */
  onTurnBargeIn: () => boolean;
  onGreeting: (text: string) => void;
}

export interface VoiceTurnLatencyControls {
  beginTurn: () => void;
  markFirstDelta: () => void;
  markToolRound: () => void;
  markInterimSentences: (count: number) => void;
  setNeoChatId: (neoChatId: number) => void;
  abandon: () => void;
}

interface TurnLatencyMarks {
  speechEnd: number;
  request: number;
  firstDelta: number | null;
  firstSentence: number | null;
  ttsFirstByte: number | null;
  toolRounds: number;
  interimSentencesSpoken: number;
  neoChatId: number | null;
}

export interface VoiceSession {
  isSupported: boolean;
  isDictationSupported: boolean;
  isDictationStarting: boolean;
  mode: VoiceSessionMode;
  status: VoiceSessionStatus;
  interimTranscript: string;
  lastError: VoiceSessionError;
  dictationAnalyser: AnalyserNode | null;
  listAudioInputs: () => Promise<AudioInputDevice[]>;
  startDictation: () => void;
  stopDictation: (accept: boolean) => void;
  startConversation: () => void;
  speak: (text: string) => number;
  stop: () => void;
  turnLatency: VoiceTurnLatencyControls;
  /** Live voice/dictation session id, or null when none is running. */
  getVoiceSessionId: () => string | null;
}

const VOICE_IDLE_SUSPEND_MS = 180000;
const VOICE_IDLE_CHECK_INTERVAL_MS = 10000;
const VOICE_RECONNECT_MAX_ATTEMPTS = 3;
const VOICE_RECONNECT_BACKOFF_MS = [500, 1000, 2000] as const;
// Batch dictation floor: used ONLY where the streaming path cannot run. No interim
// transcript exists on this path, so none is shown — the text lands on accept.
const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
] as const;
const RECORDER_TIMESLICE_MS = 1000;
// A MediaSource that never fires 'sourceopen' would strand the drain loop forever and kill
// every later sentence, so the wait is bounded and falls through to the blob path.
const MEDIA_SOURCE_OPEN_TIMEOUT_MS = 3000;
// 44-byte WAV header with zero samples: a play() of this inside the click gesture is
// what lets iOS play every later synthesised sentence on the same element.
const SILENT_WAV_DATA_URL = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';

function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return RECORDER_MIME_CANDIDATES.find((candidate) => MediaRecorder.isTypeSupported(candidate));
}

function detectBatchSupport(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

async function acquireMicStream(
  deviceId: string | null,
  constraints?: MediaTrackConstraints
): Promise<MediaStream> {
  const base: MediaTrackConstraints = { ...(constraints ?? {}) };
  const fallbackAudio: MediaStreamConstraints['audio'] = Object.keys(base).length > 0 ? base : true;
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: deviceId ? { ...base, deviceId: { exact: deviceId } } : fallbackAudio,
    });
  } catch (err) {
    if (deviceId && err instanceof Error && err.name === 'OverconstrainedError') {
      return navigator.mediaDevices.getUserMedia({ audio: fallbackAudio });
    }
    throw err;
  }
}

function appendChunk(sourceBuffer: SourceBuffer, chunk: Uint8Array): Promise<void> {
  return new Promise((resolve, reject) => {
    sourceBuffer.addEventListener('updateend', () => resolve(), { once: true });
    sourceBuffer.addEventListener('error', () => reject(new Error('SourceBuffer append failed')), { once: true });
    sourceBuffer.appendBuffer(chunk);
  });
}

async function pumpBody(
  body: ReadableStream<Uint8Array>,
  sourceBuffer: SourceBuffer,
  mediaSource: MediaSource,
  signal: AbortSignal
): Promise<void> {
  const reader = body.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (mediaSource.readyState !== 'open') return;
      await appendChunk(sourceBuffer, value);
    }
    if (mediaSource.readyState === 'open' && !sourceBuffer.updating) mediaSource.endOfStream();
  } catch (err) {
    // Barge-in and teardown abort the fetch; that is a quiet cancel, not a failure.
    if (signal.aborted) return;
    throw err;
  }
}

export function useVoiceSession({
  enabled,
  suspended,
  dictationDeviceId,
  activeNeoChatId,
  onNeoChatBound,
  onDictationText,
  onUtterance,
  onTurnBargeIn,
  onGreeting,
}: UseVoiceSessionOptions): VoiceSession {
  const [mode, setModeState] = useState<VoiceSessionMode>('off');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isDictationStarting, setIsDictationStarting] = useState(false);
  const [lastError, setLastError] = useState<VoiceSessionError>(null);
  const [dictationAnalyser, setDictationAnalyser] = useState<AnalyserNode | null>(null);

  const modeRef = useRef<VoiceSessionMode>('off');
  const suspendedRef = useRef(suspended);
  const pendingUtteranceRef = useRef('');
  const spokenScriptRef = useRef<SpokenScript>({ words: new Set(), joined: '' });
  const echoTailUntilRef = useRef(0);
  const maybeResumeListeningRef = useRef<() => void>(() => undefined);
  const onUtteranceRef = useRef(onUtterance);
  const onDictationTextRef = useRef(onDictationText);
  const dictationDeviceIdRef = useRef(dictationDeviceId);
  const activeNeoChatIdRef = useRef(activeNeoChatId);
  const onNeoChatBoundRef = useRef(onNeoChatBound);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const dictationHandleRef = useRef<SttStreamHandle | null>(null);
  const dictationSessionIdRef = useRef<string | null>(null);
  const dictationStartingRef = useRef(false);
  const dictationEpochRef = useRef(0);
  const dictationRecorderRef = useRef<MediaRecorder | null>(null);
  const dictationChunksRef = useRef<Blob[]>([]);
  const dictationFinalRef = useRef('');
  const dictationLiveRef = useRef('');
  const stopDictationRef = useRef<(accept: boolean) => void>(() => undefined);
  const conversationEpochRef = useRef(0);
  const voiceSessionIdRef = useRef<string | null>(null);
  const sttHandleRef = useRef<SttStreamHandle | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const convStreamRef = useRef<MediaStream | null>(null);
  const convAudioContextRef = useRef<AudioContext | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const lastVoiceActivityRef = useRef(0);
  const idleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopRef = useRef<() => void>(() => undefined);
  const playbackQueueRef = useRef<string[]>([]);
  const activeUtteranceRef = useRef<string | null>(null);
  const isSpeakingRef = useRef(false);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const currentObjectUrlRef = useRef<string | null>(null);
  const drainingRef = useRef(false);
  const playbackDoneResolverRef = useRef<((outcome: 'ended' | 'error' | 'cancelled') => void) | null>(null);
  const prefetchRef = useRef<{ text: string; controller: AbortController; promise: Promise<Response> } | null>(null);
  const ttsAbortControllersRef = useRef<Set<AbortController>>(new Set());
  const ttsMaxCharsRef = useRef<number | null>(null);
  const ttsMaxCharsMissingLoggedRef = useRef(false);
  // Armed by the barge-in branch of handleTurnMessage ONLY when the panel actually
  // aborted the turn; cleared by the next effective flushUtterance. While armed,
  // speak() drops everything: sentences emitted by a dying turn stream between the
  // barge and the member's flush must not play.
  const speechSuppressedRef = useRef(false);
  const onTurnBargeInRef = useRef(onTurnBargeIn);
  const onGreetingRef = useRef(onGreeting);
  const speechEndAtRef = useRef<number | null>(null);
  const pendingSpeechEndRef = useRef<number | null>(null);
  const turnLatencyRef = useRef<TurnLatencyMarks | null>(null);

  onUtteranceRef.current = onUtterance;
  onTurnBargeInRef.current = onTurnBargeIn;
  onGreetingRef.current = onGreeting;
  onDictationTextRef.current = onDictationText;
  dictationDeviceIdRef.current = dictationDeviceId;
  activeNeoChatIdRef.current = activeNeoChatId;
  onNeoChatBoundRef.current = onNeoChatBound;

  const isSupported = detectStreamingSupport();
  // Dictation has a floor: a browser that cannot stream can still record and transcribe in
  // one pass. Conversation has no such floor and stays streaming-only.
  const isDictationSupported = isSupported || detectBatchSupport();

  const setMode = useCallback((next: VoiceSessionMode) => {
    modeRef.current = next;
    setModeState(next);
  }, []);

  const flushUtterance = useCallback(() => {
    if (suspendedRef.current) return;
    speechSuppressedRef.current = false;
    const text = pendingUtteranceRef.current.trim();
    pendingUtteranceRef.current = '';
    setInterimTranscript('');
    if (text) {
      pendingSpeechEndRef.current = speechEndAtRef.current;
      speechEndAtRef.current = null;
      onUtteranceRef.current(text);
    }
  }, []);

  const maybeResumeListening = useCallback(() => {
    if (modeRef.current !== 'conversation' || suspendedRef.current) {
      return;
    }
    if (pendingUtteranceRef.current.trim()) {
      flushUtterance();
    }
  }, [flushUtterance]);

  maybeResumeListeningRef.current = maybeResumeListening;

  const updateSpeaking = useCallback(() => {
    const speaking = playbackQueueRef.current.length > 0 || activeUtteranceRef.current !== null;
    isSpeakingRef.current = speaking;
    setIsSpeaking(speaking);
  }, []);

  const revokeCurrentObjectUrl = useCallback(() => {
    if (currentObjectUrlRef.current) {
      URL.revokeObjectURL(currentObjectUrlRef.current);
      currentObjectUrlRef.current = null;
    }
  }, []);

  const cancelSpeech = useCallback(() => {
    for (const controller of ttsAbortControllersRef.current) controller.abort();
    ttsAbortControllersRef.current.clear();
    turnLatencyRef.current = null;
    pendingSpeechEndRef.current = null;
    speechEndAtRef.current = null;
    playbackQueueRef.current = [];
    prefetchRef.current = null;
    audioElementRef.current?.pause();
    playbackDoneResolverRef.current?.('cancelled');
    revokeCurrentObjectUrl();
    activeUtteranceRef.current = null;
    echoTailUntilRef.current = Date.now() + SPEECH_ECHO_TAIL_MS;
    updateSpeaking();
  }, [revokeCurrentObjectUrl, updateSpeaking]);

  const dictationText = useCallback(
    () => [dictationFinalRef.current, dictationLiveRef.current].filter(Boolean).join(' ').trim(),
    []
  );

  const teardownDictationCapture = useCallback(() => {
    const recorder = dictationRecorderRef.current;
    dictationRecorderRef.current = null;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    dictationChunksRef.current = [];
    dictationHandleRef.current?.teardown();
    dictationHandleRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    void audioContextRef.current?.close().catch(() => undefined);
    audioContextRef.current = null;
    setDictationAnalyser(null);
    dictationFinalRef.current = '';
    dictationLiveRef.current = '';
    const voiceSessionId = dictationSessionIdRef.current;
    dictationSessionIdRef.current = null;
    if (voiceSessionId) {
      void endVoiceSessionApi(voiceSessionId).catch((err: unknown) => {
        // The backend voice-session reaper finalises sessions whose end call never lands.
        console.error('dictation session end failed; reaper will finalise', err);
      });
    }
  }, []);

  const teardownConversation = useCallback((endSession: boolean) => {
    conversationEpochRef.current += 1;
    if (idleIntervalRef.current !== null) {
      clearInterval(idleIntervalRef.current);
      idleIntervalRef.current = null;
    }
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    sttHandleRef.current?.teardown();
    sttHandleRef.current = null;
    convStreamRef.current?.getTracks().forEach((track) => track.stop());
    convStreamRef.current = null;
    void convAudioContextRef.current?.close().catch(() => undefined);
    convAudioContextRef.current = null;
    reconnectAttemptsRef.current = 0;
    const voiceSessionId = voiceSessionIdRef.current;
    voiceSessionIdRef.current = null;
    if (endSession && voiceSessionId) {
      void endVoiceSessionApi(voiceSessionId).catch((err: unknown) => {
        // The backend voice-session reaper finalises sessions whose end call never lands.
        console.error('voice session end failed; reaper will finalise', err);
      });
    }
  }, []);

  const stop = useCallback(() => {
    modeRef.current = 'off';
    // Supersede any dictation still in setup so it tears down instead of arriving late.
    dictationEpochRef.current += 1;
    pendingUtteranceRef.current = '';
    speechSuppressedRef.current = false;
    cancelSpeech();
    teardownConversation(true);
    teardownDictationCapture();
    setIsFinalizing(false);
    setInterimTranscript('');
    setLastError(null);
    setModeState('off');
  }, [cancelSpeech, teardownConversation, teardownDictationCapture]);

  const failSession = useCallback(
    (error: Exclude<VoiceSessionError, null>) => {
      modeRef.current = 'off';
      pendingUtteranceRef.current = '';
      speechSuppressedRef.current = false;
      cancelSpeech();
      teardownConversation(true);
      teardownDictationCapture();
      setIsFinalizing(false);
      setInterimTranscript('');
      setLastError(error);
      setModeState('off');
    },
    [cancelSpeech, teardownConversation, teardownDictationCapture]
  );

  stopRef.current = stop;

  const ensureAudioElement = useCallback((): HTMLAudioElement => {
    if (audioElementRef.current) return audioElementRef.current;
    // Playback through the page's audio engine is the AEC reference signal — the
    // entire point of this series (readme/voice.md).
    const element = new Audio();
    element.setAttribute('playsinline', '');
    element.addEventListener('ended', () => {
      playbackDoneResolverRef.current?.('ended');
    });
    element.addEventListener('error', () => {
      playbackDoneResolverRef.current?.('error');
    });
    element.addEventListener('playing', () => {
      const marks = turnLatencyRef.current;
      const voiceSessionId = voiceSessionIdRef.current;
      if (
        !marks ||
        !voiceSessionId ||
        marks.firstDelta === null ||
        marks.firstSentence === null ||
        marks.ttsFirstByte === null ||
        marks.neoChatId === null
      ) {
        return;
      }
      turnLatencyRef.current = null;
      const playing = performance.now();
      const body: VoiceTurnLatencyRequest = {
        neoChatId: marks.neoChatId,
        toolRounds: marks.toolRounds,
        interimSentencesSpoken: marks.interimSentencesSpoken,
        eotToRequestMs: Math.round(marks.request - marks.speechEnd),
        requestToFirstDeltaMs: Math.round(marks.firstDelta - marks.request),
        firstDeltaToFirstSentenceMs: Math.round(marks.firstSentence - marks.firstDelta),
        firstSentenceToTtsFirstByteMs: Math.round(marks.ttsFirstByte - marks.firstSentence),
        ttsFirstByteToPlayingMs: Math.round(playing - marks.ttsFirstByte),
        speechEndToFirstAudioMs: Math.round(playing - marks.speechEnd),
      };
      if (Object.values(body).some((value) => value < 0)) return;
      // Telemetry exception: a lost latency sample must never surface to the member.
      void postVoiceTurnLatency(voiceSessionId, body).catch(() => undefined);
    });
    audioElementRef.current = element;
    return element;
  }, []);

  const primeAudioElement = useCallback((element: HTMLAudioElement) => {
    element.src = SILENT_WAV_DATA_URL;
    void element.play().catch(() => undefined);
  }, []);

  const startSynthesis = useCallback((voiceSessionId: string, text: string) => {
    const controller = new AbortController();
    ttsAbortControllersRef.current.add(controller);
    const promise = synthesizeVoiceTts({ voiceSessionId, text }, controller.signal);
    // Parked handler: an aborted prefetch has no consumer, so this keeps the rejection from
    // going unhandled; a consuming await still sees the same rejection.
    void promise.catch(() => {
      ttsAbortControllersRef.current.delete(controller);
    });
    return { text, controller, promise };
  }, []);

  const playBlob = useCallback(
    async (blob: Blob, element: HTMLAudioElement, done: Promise<'ended' | 'error' | 'cancelled'>) => {
      const url = URL.createObjectURL(blob);
      currentObjectUrlRef.current = url;
      element.src = url;
      await element.play();
      return done;
    },
    []
  );

  const playResponse = useCallback(
    async (response: Response, controller: AbortController): Promise<'ended' | 'error' | 'cancelled'> => {
      const element = ensureAudioElement();
      const done = new Promise<'ended' | 'error' | 'cancelled'>((resolve) => {
        playbackDoneResolverRef.current = resolve;
      });
      const contentType = response.headers.get('content-type') ?? '';
      const body = response.body;
      const canStream =
        body !== null &&
        contentType.startsWith('audio/mpeg') &&
        typeof MediaSource !== 'undefined' &&
        MediaSource.isTypeSupported('audio/mpeg');
      try {
        if (!canStream || body === null) {
          // The sanctioned fallback: browsers whose MediaSource cannot play mp3 keep the
          // whole-blob path that every lane used before streaming playback.
          return await playBlob(await response.blob(), element, done);
        }
        const mediaSource = new MediaSource();
        const url = URL.createObjectURL(mediaSource);
        currentObjectUrlRef.current = url;
        const opened = new Promise<'opened'>((resolve) => {
          mediaSource.addEventListener('sourceopen', () => resolve('opened'), { once: true });
        });
        element.src = url;
        // 'sourceopen' is the one wait in the drain that nothing else can settle: a cancel
        // pauses the element and resolves `done`, but never this. Race it against both so a
        // MediaSource that never opens cannot strand drainingRef and kill all later speech.
        let openTimer: ReturnType<typeof setTimeout> | undefined;
        const openTimedOut = new Promise<'timeout'>((resolve) => {
          openTimer = setTimeout(() => resolve('timeout'), MEDIA_SOURCE_OPEN_TIMEOUT_MS);
        });
        const openOutcome = await Promise.race([opened, openTimedOut, done]);
        clearTimeout(openTimer);
        if (openOutcome !== 'opened') {
          // A settled `done` won the race: the member cancelled, or playback already ended.
          if (openOutcome !== 'timeout') return openOutcome;
          // The body is still untouched, so the blob path can consume it.
          revokeCurrentObjectUrl();
          return await playBlob(await response.blob(), element, done);
        }
        let sourceBuffer: SourceBuffer;
        try {
          sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
        } catch {
          // The body is still untouched, so the blob path can consume it.
          revokeCurrentObjectUrl();
          return await playBlob(await response.blob(), element, done);
        }
        const pump = pumpBody(body, sourceBuffer, mediaSource, controller.signal);
        const playRequest = element.play().then(
          () => done,
          (err: unknown) => {
            // cancelSpeech aborts the fetch before it pauses the element, so a play() rejected by
            // that pause is our own cancel, not a playback failure.
            if (controller.signal.aborted) return done;
            throw err;
          }
        );
        // A genuine mid-stream pump failure rejects here so the drain surfaces it.
        return await Promise.race([done, pump.then(() => done), playRequest]);
      } finally {
        playbackDoneResolverRef.current = null;
        revokeCurrentObjectUrl();
      }
    },
    [ensureAudioElement, playBlob, revokeCurrentObjectUrl]
  );

  const drainQueue = useCallback(async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    const epoch = conversationEpochRef.current;
    try {
      while (playbackQueueRef.current.length > 0) {
        if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') return;
        const text = playbackQueueRef.current.shift();
        if (text === undefined) return;
        activeUtteranceRef.current = text;
        updateSpeaking();
        try {
          const voiceSessionId = voiceSessionIdRef.current;
          if (!voiceSessionId) {
            throw new Error('No active voice session for TTS playback');
          }
          const prefetched = prefetchRef.current;
          prefetchRef.current = null;
          const inflight =
            prefetched && prefetched.text === text ? prefetched : startSynthesis(voiceSessionId, text);
          let response: Response;
          try {
            response = await inflight.promise;
          } catch (err) {
            if (inflight.controller.signal.aborted) return;
            throw err;
          }
          if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') return;
          if (activeUtteranceRef.current !== text) return; // cancelled while fetching
          const marks = turnLatencyRef.current;
          if (marks && marks.firstSentence !== null && marks.ttsFirstByte === null) {
            marks.ttsFirstByte = performance.now();
          }
          // Prefetch exactly one ahead: the next sentence synthesises while this
          // one plays. Never the whole queue — barge-in would waste synth spend.
          const nextText = playbackQueueRef.current[0];
          if (nextText !== undefined) {
            prefetchRef.current = startSynthesis(voiceSessionId, nextText);
          }
          try {
            const outcome = await playResponse(response, inflight.controller);
            if (outcome === 'error') {
              throw new Error('Audio element playback error');
            }
            if (outcome === 'cancelled') {
              return;
            }
          } finally {
            // The body keeps streaming while the sentence plays, so the fetch stays
            // abortable until playback has finished.
            ttsAbortControllersRef.current.delete(inflight.controller);
          }
        } catch (err) {
          console.error('voice TTS playback failed; dropping utterance', err);
          playbackDoneResolverRef.current = null;
          revokeCurrentObjectUrl();
          if (conversationEpochRef.current === epoch) {
            setLastError('tts-failed');
          }
        } finally {
          activeUtteranceRef.current = null;
          updateSpeaking();
        }
      }
    } finally {
      drainingRef.current = false;
      if (playbackQueueRef.current.length === 0 && activeUtteranceRef.current === null) {
        echoTailUntilRef.current = Date.now() + SPEECH_ECHO_TAIL_MS;
        maybeResumeListeningRef.current();
      }
    }
  }, [playResponse, revokeCurrentObjectUrl, startSynthesis, updateSpeaking]);

  const speak = useCallback(
    (text: string): number => {
      if (modeRef.current !== 'conversation') return 0;
      if (speechSuppressedRef.current) return 0;
      const speakable = toSpeakableText(text);
      if (!speakable) return 0;
      const scriptWords = normalizeSpokenWords(speakable);
      if (!isSpeakingRef.current) {
        spokenScriptRef.current = { words: new Set(scriptWords), joined: scriptWords.join(' ') };
      } else {
        const script = spokenScriptRef.current;
        for (const word of scriptWords) {
          script.words.add(word);
        }
        script.joined = [script.joined, scriptWords.join(' ')].filter(Boolean).join(' ');
      }
      const maxChars = ttsMaxCharsRef.current;
      if (maxChars === null) {
        // speak() runs inside the panel's stream loop, where a throw becomes a member-facing
        // turn failure. Reachable in the window between setMode('conversation') and the mint
        // resolving, so a silent no-op beats killing the turn. Logged once: the stream loop
        // calls speak() per sentence and would otherwise flood the console.
        if (!ttsMaxCharsMissingLoggedRef.current) {
          ttsMaxCharsMissingLoggedRef.current = true;
          console.error(
            'voice speak() skipped: no ttsMaxChars yet (mint has not resolved)',
            { mode: modeRef.current, hasVoiceSessionId: voiceSessionIdRef.current !== null }
          );
        }
        return 0;
      }
      let queued = 0;
      for (const sentence of capSpeakableSentences(splitSpeakableSentences(speakable), maxChars)) {
        const trimmed = sentence.trim();
        if (trimmed) {
          playbackQueueRef.current.push(trimmed);
          queued += 1;
        }
      }
      const marks = turnLatencyRef.current;
      if (queued > 0 && marks && marks.firstSentence === null) {
        marks.firstSentence = performance.now();
      }
      updateSpeaking();
      void drainQueue();
      return queued;
    },
    [drainQueue, updateSpeaking]
  );

  const handleTurnMessage = useCallback(
    (turn: SttTurn) => {
      lastVoiceActivityRef.current = Date.now();
      // Speech during a suspended turn (Neo thinking, an upload, hydration) is never dropped:
      // it accumulates in pendingUtteranceRef and the interim transcript, and
      // maybeResumeListening submits it as the next turn when suspension lifts.
      const heard = (turn.transcript ?? '').trim();
      if (!heard) return;
      // Fallback defence only: with the AEC reference signal in place (see
      // readme/voice.md) echo is cancelled at the waveform
      // level; this transcript matching is residual insurance while speaking and
      // during the short tail below.
      if (isSpeakingRef.current) {
        if (matchesSpokenScript(heard, spokenScriptRef.current)) {
          return;
        }
        const combined = [pendingUtteranceRef.current, heard].filter(Boolean).join(' ').trim();
        if (combined.length < INTERRUPT_TRANSCRIPT_MIN_LENGTH) {
          pendingUtteranceRef.current = combined;
          return;
        }
        // Barge-in. Deliberate delta from the OS-voice era, which discarded the
        // trigger fragment: with AEC plus real STT the interrupting fragment is
        // genuine member speech and must land in the utterance — fall through.
        cancelSpeech();
        // Suppression exists to silence a DYING turn stream: once the turn is aborted, the
        // sentences still arriving on the wire are from a reply the member has cancelled.
        // A turn that was NOT aborted is not dying — the panel declines the abort to let a
        // tool round finish, and that round's final answer is still wanted. So cancelSpeech()
        // always runs (stopping the interim sentence is the whole point of interrupting) but
        // suppression is armed only when the callback says it killed the turn.
        speechSuppressedRef.current = onTurnBargeInRef.current();
      }
      if (
        Date.now() < echoTailUntilRef.current &&
        matchesSpokenScript(heard, spokenScriptRef.current)
      ) {
        echoTailUntilRef.current = Date.now() + SPEECH_ECHO_TAIL_MS;
        return;
      }
      if (turn.endOfTurn) {
        speechEndAtRef.current = performance.now();
        pendingUtteranceRef.current = [pendingUtteranceRef.current, heard]
          .filter(Boolean)
          .join(' ')
          .trim();
        flushUtterance();
        return;
      }
      setInterimTranscript(
        [pendingUtteranceRef.current, heard].filter(Boolean).join(' ').trim()
      );
    },
    [cancelSpeech, flushUtterance]
  );

  const openStreamRef = useRef<(minted: VoiceSessionMintResponse, epoch: number) => Promise<void>>(
    async () => undefined
  );
  const scheduleReconnectRef = useRef<(epoch: number) => void>(() => undefined);

  const openStream = useCallback(
    async (minted: VoiceSessionMintResponse, epoch: number) => {
      const stream = convStreamRef.current;
      const context = convAudioContextRef.current;
      if (!stream || !context) {
        throw new Error('Conversation capture is not initialised');
      }
      sttHandleRef.current?.teardown();
      sttHandleRef.current = null;
      const handle = await openSttStream({
        stream,
        context,
        provider: minted.sttProvider,
        auth: minted.sttAuth,
        token: minted.sttToken,
        socketUrl: minted.sttSocketUrl,
        onBegin: () => {
          reconnectAttemptsRef.current = 0;
        },
        onTurn: (turn) => {
          if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') return;
          handleTurnMessage(turn);
        },
        onError: () => {
          if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') return;
          scheduleReconnectRef.current(epoch);
        },
      });
      if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') {
        handle.teardown();
        return;
      }
      sttHandleRef.current = handle;
    },
    [handleTurnMessage]
  );
  openStreamRef.current = openStream;

  const reconnect = useCallback(
    async (epoch: number) => {
      if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') return;
      const voiceSessionId = voiceSessionIdRef.current;
      if (!voiceSessionId) return;
      try {
        const minted = await mintVoiceSession({ voiceSessionId });
        if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') return;
        ttsMaxCharsRef.current = minted.ttsMaxChars;
        await openStreamRef.current(minted, epoch);
      } catch (err) {
        console.error('voice session reconnect attempt failed', err);
        scheduleReconnectRef.current(epoch);
      }
    },
    []
  );

  const scheduleReconnect = useCallback(
    (epoch: number) => {
      // A failing socket fires both onerror and onclose; the first call owns the retry.
      if (reconnectTimerRef.current !== null) return;
      sttHandleRef.current?.teardown();
      sttHandleRef.current = null;
      const attempt = reconnectAttemptsRef.current;
      if (attempt >= VOICE_RECONNECT_MAX_ATTEMPTS) {
        failSession('voice-session-failed');
        return;
      }
      const backoff = VOICE_RECONNECT_BACKOFF_MS[attempt];
      if (backoff === undefined) {
        throw new Error(`No reconnect backoff configured for attempt ${attempt}`);
      }
      reconnectAttemptsRef.current = attempt + 1;
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        void reconnect(epoch);
      }, backoff);
    },
    [failSession, reconnect]
  );
  scheduleReconnectRef.current = scheduleReconnect;

  const startConversation = useCallback(() => {
    if (!enabled || !isSupported) return;
    if (modeRef.current !== 'off') stop();
    setLastError(null);
    setMode('conversation');
    const epoch = ++conversationEpochRef.current;
    // Safari only hands out an AudioContext and unlocks an audio element from inside
    // the click gesture; the first await below leaves it, so both happen here.
    const audioContext = new AudioContext();
    convAudioContextRef.current = audioContext;
    primeAudioElement(ensureAudioElement());
    void (async () => {
      let minted: VoiceSessionMintResponse;
      try {
        const neoChatId = activeNeoChatIdRef.current;
        minted = await mintVoiceSession(neoChatId !== undefined ? { neoChatId } : {});
      } catch (err) {
        console.error('voice session mint failed', err);
        if (conversationEpochRef.current === epoch) failSession('voice-session-failed');
        return;
      }
      if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') {
        void endVoiceSessionApi(minted.voiceSessionId).catch(() => undefined);
        return;
      }
      voiceSessionIdRef.current = minted.voiceSessionId;
      ttsMaxCharsRef.current = minted.ttsMaxChars;
      // Null until the member actually speaks: the mint no longer opens a chat for a greeting.
      if (minted.neoChatId !== null) onNeoChatBoundRef.current(minted.neoChatId);

      let stream: MediaStream;
      try {
        stream = await acquireMicStream(dictationDeviceIdRef.current, {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        });
      } catch {
        if (conversationEpochRef.current === epoch) failSession('permission-denied');
        return;
      }
      if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      convStreamRef.current = stream;

      if (audioContext.state === 'suspended') await audioContext.resume();
      if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') return;
      try {
        await openStreamRef.current(minted, epoch);
      } catch (err) {
        console.error('voice capture failed to open', err);
        if (conversationEpochRef.current === epoch) failSession('voice-session-failed');
        return;
      }
      if (conversationEpochRef.current !== epoch || modeRef.current !== 'conversation') return;

      if (minted.greeting !== null) {
        onGreetingRef.current(minted.greeting);
        speak(minted.greeting);
      }

      lastVoiceActivityRef.current = Date.now();
      idleIntervalRef.current = setInterval(() => {
        if (modeRef.current !== 'conversation') return;
        if (isSpeakingRef.current) {
          lastVoiceActivityRef.current = Date.now();
          return;
        }
        if (Date.now() - lastVoiceActivityRef.current > VOICE_IDLE_SUSPEND_MS) {
          // Idle auto-suspend: sessions bill on connection duration, so an abandoned
          // open mic is a slow leak. Full teardown including the end call; resuming
          // via the mic button mints a fresh session.
          stopRef.current();
        }
      }, VOICE_IDLE_CHECK_INTERVAL_MS);
    })();
  }, [enabled, isSupported, stop, setMode, failSession, speak, ensureAudioElement, primeAudioElement]);

  const listAudioInputs = useCallback(async (): Promise<AudioInputDevice[]> => {
    if (!isDictationSupported) return [];
    const readInputs = async () =>
      (await navigator.mediaDevices.enumerateDevices()).filter(
        (device) => device.kind === 'audioinput'
      );
    let inputs = await readInputs();
    if (inputs.length === 0 || inputs.some((device) => !device.label)) {
      try {
        const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
        probe.getTracks().forEach((track) => track.stop());
        inputs = await readInputs();
      } catch {
        setLastError('permission-denied');
        return [];
      }
    }
    return inputs.map((device, index) => ({
      deviceId: device.deviceId,
      label: device.label || `Microphone ${index + 1}`,
    }));
  }, [isDictationSupported]);

  const startDictation = useCallback(() => {
    if (!enabled || !isDictationSupported || modeRef.current !== 'off') return;
    // The setup window spans three awaits during which mode is still 'off', so a second
    // click would otherwise pass every guard and mint a second VoiceSession.
    if (dictationStartingRef.current) return;
    dictationStartingRef.current = true;
    setIsDictationStarting(true);
    const epoch = ++dictationEpochRef.current;
    setLastError(null);
    const deviceId = dictationDeviceIdRef.current;
    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;
    void (async () => {
      try {
        let minted: VoiceSessionMintResponse;
        try {
          const neoChatId = activeNeoChatIdRef.current;
          minted = await mintVoiceSession({ kind: 'DICTATION', ...(neoChatId !== undefined ? { neoChatId } : {}) });
        } catch (err) {
          console.error('dictation session mint failed', err);
          setLastError('voice-session-failed');
          teardownDictationCapture();
          return;
        }
        if (dictationEpochRef.current !== epoch) {
          // Superseded (cancel or stop during setup): hand the id to the teardown so the
          // session that was already minted is ended rather than left to the reaper.
          dictationSessionIdRef.current = minted.voiceSessionId;
          teardownDictationCapture();
          return;
        }
        dictationSessionIdRef.current = minted.voiceSessionId;
        if (minted.neoChatId !== null) onNeoChatBoundRef.current(minted.neoChatId);

        let stream: MediaStream;
        try {
          stream = await acquireMicStream(deviceId);
        } catch {
          setLastError('permission-denied');
          teardownDictationCapture();
          return;
        }
        if (dictationEpochRef.current !== epoch) {
          stream.getTracks().forEach((track) => track.stop());
          teardownDictationCapture();
          return;
        }
        mediaStreamRef.current = stream;

        if (audioContext.state === 'suspended') await audioContext.resume();
        if (dictationEpochRef.current !== epoch) {
          teardownDictationCapture();
          return;
        }
        // The batch floor is selected by exactly two conditions and nothing else: the browser
        // cannot stream at all, or the STT socket refused to open. Everything else keeps the
        // streaming path, interim transcript included.
        let handle: SttStreamHandle | null = null;
        if (isSupported) {
          try {
            handle = await openSttStream({
              stream,
              context: audioContext,
              provider: minted.sttProvider,
              auth: minted.sttAuth,
              token: minted.sttToken,
              socketUrl: minted.sttSocketUrl,
              onBegin: () => undefined,
              onTurn: (turn) => {
                if (dictationHandleRef.current !== handle) return;
                if (turn.endOfTurn) {
                  dictationFinalRef.current = [dictationFinalRef.current, turn.transcript].filter(Boolean).join(' ').trim();
                  dictationLiveRef.current = '';
                } else {
                  dictationLiveRef.current = turn.transcript;
                }
                onDictationTextRef.current(dictationText(), false);
              },
              onError: () => {
                if (dictationHandleRef.current !== handle) return;
                setLastError('voice-session-failed');
                stopDictationRef.current(false);
              },
            });
          } catch (err) {
            console.error('dictation streaming capture failed to open; falling back to batch', err);
          }
        }
        if (dictationEpochRef.current !== epoch) {
          handle?.teardown();
          teardownDictationCapture();
          return;
        }
        if (handle !== null) {
          dictationHandleRef.current = handle;
          setDictationAnalyser(handle.analyser);
          dictationFinalRef.current = '';
          dictationLiveRef.current = '';
          setIsFinalizing(false);
          setMode('dictation');
          return;
        }

        // Batch floor. The analyser is a real level meter off the same stream — the composer
        // shows genuine recording state; what is absent is the interim transcript, and no
        // stand-in for it is invented.
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        source.connect(analyser);
        const mimeType = pickRecorderMimeType();
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        dictationChunksRef.current = [];
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) dictationChunksRef.current.push(event.data);
        };
        recorder.start(RECORDER_TIMESLICE_MS);
        dictationRecorderRef.current = recorder;
        setDictationAnalyser(analyser);
        dictationFinalRef.current = '';
        dictationLiveRef.current = '';
        setIsFinalizing(false);
        setMode('dictation');
      } finally {
        // Unconditional: the guard blocks any competing start for as long as it is armed,
        // so this start always owns it — including on the superseded path, where a cancel
        // bumped the epoch but started nothing. A finally that only clears a flag catches
        // every exit path without swallowing anything.
        dictationStartingRef.current = false;
        setIsDictationStarting(false);
      }
    })();
  }, [enabled, isSupported, isDictationSupported, setMode, teardownDictationCapture, dictationText]);

  const stopDictation = useCallback(
    (accept: boolean) => {
      if (dictationStartingRef.current && modeRef.current !== 'dictation') {
        // Pressed during the setup window: nothing has been captured yet, so accept and
        // cancel are the same act. Bumping the epoch supersedes the in-flight start, whose
        // own guard tears the capture down and ends any session it already minted.
        dictationEpochRef.current += 1;
        return;
      }
      if (modeRef.current !== 'dictation') return;

      if (!accept) {
        teardownDictationCapture();
        modeRef.current = 'off';
        setIsFinalizing(false);
        setModeState('off');
        return;
      }

      setIsFinalizing(true);
      const recorder = dictationRecorderRef.current;
      if (recorder !== null) {
        void (async () => {
          // A recorder that is already inactive has fired its 'stop' event before this
          // listener could exist — a track ending mid-dictation (headset unplugged, device
          // removed) auto-stops it — and DOM events are not replayed, so awaiting one here
          // would never settle and would strand the composer on the accept spinner.
          const stopped =
            recorder.state === 'inactive'
              ? Promise.resolve()
              : new Promise<void>((resolve) => {
                  recorder.addEventListener('stop', () => resolve(), { once: true });
                });
          if (recorder.state !== 'inactive') recorder.stop();
          await stopped;
          const blob = new Blob(dictationChunksRef.current, { type: recorder.mimeType });
          // The session ends at capture-stop, BEFORE transcription: billed seconds are the
          // time the microphone was open, not the provider's turnaround on top of it. Mode
          // stays 'dictation' until the text lands so the composer keeps showing the
          // finalizing spinner, exactly as the streaming path does.
          teardownDictationCapture();
          try {
            const result = await transcribeDictationAudio(blob);
            onDictationTextRef.current(result.text, true);
          } catch (err) {
            console.error('dictation transcription failed', err);
            setLastError('transcription-failed');
          } finally {
            modeRef.current = 'off';
            setIsFinalizing(false);
            setModeState('off');
          }
        })();
        return;
      }
      const handle = dictationHandleRef.current;
      void (async () => {
        if (handle) await handle.stop();
        const text = dictationText();
        teardownDictationCapture();
        onDictationTextRef.current(text, true);
        modeRef.current = 'off';
        setIsFinalizing(false);
        setModeState('off');
      })();
    },
    [teardownDictationCapture, dictationText]
  );
  stopDictationRef.current = stopDictation;

  useEffect(() => {
    suspendedRef.current = suspended;
    if (modeRef.current !== 'conversation') return;
    if (!suspended) maybeResumeListening();
  }, [suspended, maybeResumeListening]);

  useEffect(() => {
    if (!enabled && modeRef.current !== 'off') {
      stop();
    }
  }, [enabled, stop]);

  useEffect(() => () => stop(), [stop]);

  const status: VoiceSessionStatus =
    mode === 'off'
      ? 'idle'
      : isSpeaking
        ? 'speaking'
        : mode === 'dictation' && isFinalizing
          ? 'finalizing'
          : mode === 'conversation' && suspended
            ? 'suspended'
            : 'listening';

  const turnLatency = useMemo<VoiceTurnLatencyControls>(
    () => ({
      beginTurn: () => {
        const speechEnd = pendingSpeechEndRef.current;
        pendingSpeechEndRef.current = null;
        if (speechEnd === null) return;
        turnLatencyRef.current = {
          speechEnd,
          request: performance.now(),
          firstDelta: null,
          firstSentence: null,
          ttsFirstByte: null,
          toolRounds: 0,
          interimSentencesSpoken: 0,
          neoChatId: null,
        };
      },
      markFirstDelta: () => {
        const marks = turnLatencyRef.current;
        if (marks && marks.firstDelta === null) marks.firstDelta = performance.now();
      },
      markToolRound: () => {
        const marks = turnLatencyRef.current;
        if (marks) marks.toolRounds += 1;
      },
      markInterimSentences: (count: number) => {
        const marks = turnLatencyRef.current;
        if (marks) marks.interimSentencesSpoken += count;
      },
      setNeoChatId: (neoChatId: number) => {
        const marks = turnLatencyRef.current;
        if (marks) marks.neoChatId = neoChatId;
      },
      abandon: () => {
        turnLatencyRef.current = null;
      },
    }),
    []
  );

  // The turn carries it so the backend can bind a session minted before the chat existed.
  // A ref read, so it never re-renders the panel.
  const getVoiceSessionId = useCallback(
    () => voiceSessionIdRef.current ?? dictationSessionIdRef.current,
    []
  );

  return useMemo(
    () => ({
      isSupported,
      isDictationSupported,
      isDictationStarting,
      mode,
      status,
      interimTranscript,
      lastError,
      dictationAnalyser,
      listAudioInputs,
      startDictation,
      stopDictation,
      startConversation,
      speak,
      stop,
      turnLatency,
      getVoiceSessionId,
    }),
    [
      isSupported,
      isDictationSupported,
      isDictationStarting,
      mode,
      status,
      interimTranscript,
      lastError,
      dictationAnalyser,
      listAudioInputs,
      startDictation,
      stopDictation,
      startConversation,
      speak,
      stop,
      turnLatency,
      getVoiceSessionId,
    ]
  );
}
